import React from "react";
import type { AppProps } from "next/app";
import i18next from "i18next";
import { I18nextProvider } from "react-i18next";
import { loadErrorMessages, loadDevMessages } from "@apollo/client/dev";
import {
  achievementBoxEn,
  achievementBoxRo,
  commonEn,
  commonRo,
  courseEn,
  courseRo,
  dropzoneValidationEn,
  dropzoneValidationRo,
  marketplaceEn,
  marketplaceRo,
  navbarEn,
  navbarRo,
  privacySettingsEn,
  privacySettingsRo,
  profileEn,
  profileRo,
  reelsEn,
  reelsRo,
  exerciseEn,
  exerciseRo,
  searchEn,
  searchRo,
  walletEn,
  walletRo,
  resendEmailEn,
  resendEmailRo,
  nftCardEn,
  nftCardRo,
  profileEditEn,
  profileEditRo,
  chatEn,
  chatRo,
  transferAssetEn,
  transferAssetRo,
  walletTransferCardEn,
  walletTransferCardRo,
  storeEn,
  storeRo,
  nftVoucherCardEn,
  nftVoucherCardRo,
  lastSeenCourseCardEn,
  lastSeenCourseCardRo,
  liveStreamingEn,
  liveStreamingRo,
  webinarEn,
  webinarRo,
  streamEn,
  streamRo,
  landingEn,
  landingRo,
  countdownEn,
  countdownRo,
  dynamicDateHourPickerEn,
  dynamicDateHourPickerRo,
  contactRo,
  contactEn,
  reservationEn,
  reservationRo,
  crmEn,
  crmRo,
} from "@/locales";
import { ApolloProvider } from "@apollo/client";
import "@/styles/globals.css";
import { seoDataType } from "@/utils/types/seoDataType";
import Head from "next/head";
import { useCookies } from "react-cookie";
import { useApollo } from "@/utils/hooks/useApollo";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { config } from "@fortawesome/fontawesome-svg-core";
import "@fortawesome/fontawesome-svg-core/styles.css";
import { useRouter } from "next/router";
import NProgress from "nprogress";
import { useEffect } from "react";
import "nprogress/nprogress.css"; // Import the styles
import { BottomTabsProvider } from "@/context/BottomTabsContext";
import UserProvider from "@/context/UserContext";
import { CartProvider } from "@/context/CartContext";
// import { library } from "@fortawesome/fontawesome-svg-core";
// import { fas } from "@fortawesome/free-solid-svg-icons";
// Prevent Font Awesome from adding its CSS since we did it manually above
config.autoAddCss = false;

// Add all icons to the library
// library.add(fas);

i18next.init({
  interpolation: { escapeValue: false }, // React already does escaping
  lng: "ro",
  debug: false,
  defaultNS: "privacySettings",
  ns: ["privacySettings"],
  resources: {
    en: {
      contact: contactEn,
      privacySettings: privacySettingsEn,
      navbar: navbarEn,
      achievementBox: achievementBoxEn,
      reels: reelsEn,
      marketplace: marketplaceEn,
      profile: profileEn,
      common: commonEn,
      course: courseEn,
      exercise: exerciseEn,
      dropzoneValidation: dropzoneValidationEn,
      search: searchEn,
      wallet: walletEn,
      resendEmail: resendEmailEn,
      nftCard: nftCardEn,
      profileEdit: profileEditEn,
      chat: chatEn,
      transferAsset: transferAssetEn,
      walletTransferCard: walletTransferCardEn,
      store: storeEn,
      nftVoucherCard: nftVoucherCardEn,
      lastSeenCourseCard: lastSeenCourseCardEn,
      liveStreaming: liveStreamingEn,
      webinar: webinarEn,
      stream: streamEn,
      landing: landingEn,
      countdown: countdownEn,
      dynamicDateHourPicker: dynamicDateHourPickerEn,
      reservation: reservationEn,
      crm: crmEn,
    },
    ro: {
      contact: contactRo,
      privacySettings: privacySettingsRo,
      navbar: navbarRo,
      achievementBox: achievementBoxRo,
      reels: reelsRo,
      marketplace: marketplaceRo,
      profile: profileRo,
      common: commonRo,
      course: courseRo,
      exercise: exerciseRo,
      dropzoneValidation: dropzoneValidationRo,
      search: searchRo,
      wallet: walletRo,
      resendEmail: resendEmailRo,
      nftCard: nftCardRo,
      profileEdit: profileEditRo,
      chat: chatRo,
      transferAsset: transferAssetRo,
      walletTransferCard: walletTransferCardRo,
      store: storeRo,
      nftVoucherCard: nftVoucherCardRo,
      lastSeenCourseCard: lastSeenCourseCardRo,
      liveStreaming: liveStreamingRo,
      webinar: webinarRo,
      stream: streamRo,
      landing: landingRo,
      countdown: countdownRo,
      dynamicDateHourPicker: dynamicDateHourPickerRo,
      reservation: reservationRo,
      crm: crmRo,
    },
  },
});

type Props = AppProps & {
  Component: Page;
};

if (process.env.NODE_ENV === "development") {
  // Adds messages only in a dev environment
  loadDevMessages();
  loadErrorMessages();
}

export default function App({ Component, pageProps }: Props) {
  const getLayout = Component.getLayout ?? ((page) => <>{page}</>);
  const [cookie] = useCookies(["authToken"]);
  const router = useRouter();

  const pageSeo: seoDataType = pageProps?.seo;

  const client = useApollo({
    token: cookie.authToken,
    graphqlEndpoint: `${process.env.NEXT_PUBLIC_API_URL}/graphql`,
  });

  useEffect(() => {
    const handleStart = () => {
      NProgress.start();
    };

    const handleComplete = () => {
      NProgress.done();
    };

    router.events.on("routeChangeStart", handleStart);
    router.events.on("routeChangeComplete", handleComplete);
    router.events.on("routeChangeError", handleComplete);

    return () => {
      router.events.off("routeChangeStart", handleStart);
      router.events.off("routeChangeComplete", handleComplete);
      router.events.off("routeChangeError", handleComplete);
    };
  }, [router]);

  return (
    <React.Fragment>
      <Head>
        <title>{pageSeo?.title ?? "Drimoland"}</title>
        {pageSeo?.description && (
          <meta name="description" content={pageSeo?.description} />
        )}
        <meta name="robots" content={pageSeo?.robots ?? "index, follow"} />
        {/* open graph for facebook */}
        {pageSeo?.ogTitle && (
          <meta property="og:title" content={pageSeo?.ogTitle} />
        )}

        {pageSeo?.ogDescription && (
          <meta property="og:description" content={pageSeo?.ogDescription} />
        )}

        {pageSeo?.canonicalURL ? (
          <link rel="canonical" href={pageSeo?.canonicalURL} />
        ) : (
          <link
            rel="canonical"
            href={`${process.env.NEXT_PUBLIC_URL || "https://drimoland.ro"}${router.asPath}`}
          />
        )}

        <meta property="og:type" content="website" />
        {pageSeo?.canonicalURL ? (
          <meta property="og:url" content={pageSeo?.canonicalURL} />
        ) : (
          <meta
            property="og:url"
            content={`${process.env.NEXT_PUBLIC_URL || "https://drimoland.ro"}${router.asPath}`}
          />
        )}

        {pageSeo?.imageURL && (
          <meta property="og:image" content={pageSeo?.imageURL} />
        )}

        <meta
          name="viewport"
          content={pageSeo?.viewport ?? "width=device-width, initial-scale=1"}
        />
        <link rel="icon" type="image/png" href="/img/favicon.png" sizes="any" />
      </Head>
      {client && (
        <ApolloProvider client={client}>
          <UserProvider
            authOnly={pageProps.authOnly ?? false}
            anonymousOnly={pageProps.anonymousOnly ?? false}
          >
            <CartProvider>
              <I18nextProvider i18n={i18next}>
                <BottomTabsProvider>
                  {getLayout(<Component {...pageProps} />)}
                </BottomTabsProvider>
              </I18nextProvider>
            </CartProvider>
          </UserProvider>
        </ApolloProvider>
      )}
      <ToastContainer
        position="top-right"
        theme="light"
        autoClose={3500}
        pauseOnHover={false}
      />
    </React.Fragment>
  );
}
